/**
 * Reine Regeln fuer die freie Zeilenliste der Sekundaerbrennstoffe (Phase 34, D-02/D-16/D-17/
 * D-18/D-19): Zeilen anlegen, Kategorie wechseln, nach Kategorie gruppieren, den Eintragszaehler
 * beschriften. Reines Datenmodul ohne React-Import und ohne Summenrechnung (D-22, die vier
 * Summen je Kategorie/Gesamt kommen fertig vom Server) -- gleicher Ton wie
 * `measurementBulkDelete.ts` und `usedWasteCodes.ts` in diesem Ordner.
 *
 * Der verbindliche Vertrag steht in `alternativeFuelRows.test.ts` (34-03-PLAN.md
 * <module_contract>) und ist hier umgesetzt, nicht neu entworfen.
 */

import type { AlternativeFuelEntry } from '../../../types/reports.js';
import type { FuelAshCategory } from '../../../services/fuelAshCategoriesApi.js';

/**
 * Eine Kategoriegruppe der Brennstofftabelle: die Stammdaten-Kategorie (D-02, ungenutzt bleibt
 * sichtbar und leer) sowie ihre Zeilen samt `flatIndex`.
 *
 * `flatIndex` ist die Position der Zeile im FLACHEN Formular-Array `alternativeFuels`, NICHT die
 * Position innerhalb dieser Gruppe -- daran haengen die Feldpfade `alternativeFuels.<flatIndex>.
 * <feld>` und damit die Zuordnung der Backend-Verstoesse (D-25-Fundstellen wie in
 * `usedWasteCodes.ts`). Eine Gruppierung, die stattdessen den gruppeninternen Index verwendete,
 * wuerde jedes Feld an den falschen Formularpfad haengen, sobald mehr als eine Kategorie Zeilen
 * traegt.
 *
 * `category: undefined` markiert die synthetische letzte Gruppe fuer Zeilen mit geloeschter
 * `categoryId` (siehe `groupRowsByCategory`).
 */
export interface AlternativeFuelGroup {
  category: FuelAshCategory | undefined;
  rows: { row: AlternativeFuelEntry; flatIndex: number }[];
}

/**
 * Legt eine neue Zeile an (D-16: kein Kategoriefeld je Zeile, die Kategorie steht bereits fest,
 * wenn der Melder in der jeweiligen Gruppe "Zeile hinzufuegen" waehlt).
 *
 * Der Aschegehalt uebernimmt `categoryStartValue` unveraendert -- auch `'0.00'` als echten Wert,
 * kein Falsy-Sonderfall (eine Kategorie mit Startwert Null ist etwas anderes als eine Kategorie
 * ohne Startwert). Eine Kategorie ohne Startwert (`null`) ergibt `ashContent: null`, keinen
 * Ersatzwert. Alle uebrigen Zahlenfelder starten leer, die Bezeichnung leer, der Abfallschluessel
 * `null`.
 *
 * `newRowId` wird hineingereicht statt importiert (die UUID-Erzeugung ist eine Browser-API) --
 * so bleibt das Modul ohne DOM/Browser testbar. Die Komponente reicht in Plan 34-12 die echte
 * Browser-Erzeugungsfunktion hinein.
 */
export function createAlternativeFuelRow(
  categoryId: number,
  categoryStartValue: string | null,
  newRowId: () => string,
): AlternativeFuelEntry {
  return {
    rowId: newRowId(),
    categoryId,
    name: '',
    wasteCode: null,
    amount: null,
    heatingValue: null,
    emissionFactor: null,
    biogenicContent: null,
    ashContent: categoryStartValue,
  };
}

/**
 * Vergleicht zwei Aschegehalt-Strings NUMERISCH, nicht als Zeichenkette: `'5.5'` und `'5.50'`
 * sind derselbe Wert. Ein `===`-Vergleich waere hier falsch und wuerde einen unberuehrten Wert
 * (der z. B. nach einem Zwischenspeichern mit weniger Nachkommastellen zurueckkommt) faelschlich
 * als vom Melder ueberschrieben behandeln -- der Kategoriewechsel wuerde ihn dann faelschlich
 * stehen lassen, statt ihm zu folgen.
 */
function isSameAshValue(value: string, otherValue: string): boolean {
  return parseFloat(value) === parseFloat(otherValue);
}

/**
 * Kategoriewechsel ueber den Knopf in der Aktionsspalte (D-17), kein Kategoriefeld je Zeile
 * (D-16). Wechsel auf dieselbe Kategorie liefert `null` (nichts zu tun).
 *
 * D-18: der Aschegehalt folgt der neuen Kategorie NUR, wenn er auf dem Zeile unberuehrt stand --
 * `null` (nie gesetzt) oder numerisch gleich dem Startwert der ALTEN Kategorie (siehe
 * `isSameAshValue`). Ein bereits vom Melder ueberschriebener Wert bleibt in jedem Fall stehen.
 * Hat die NEUE Kategorie selbst keinen Startwert, bleibt ein unberuehrter Aschegehalt ebenfalls
 * unveraendert stehen -- ein Nullen waere hier keine sinnvolle Vorbelegung, nur ein
 * verschwundener Wert.
 *
 * Liefert ausschliesslich `categoryId` und `ashContent` -- alle uebrigen Feldwerte der Zeile
 * (Bezeichnung, Abfallschluessel, Menge und die weiteren Stoffeigenschaften) sind vom
 * Kategoriewechsel nicht betroffen. Reine Funktion: die uebergebene Zeile wird nicht mutiert.
 */
export function applyCategoryChange(
  row: AlternativeFuelEntry,
  oldCategory: FuelAshCategory,
  newCategory: FuelAshCategory,
): Pick<AlternativeFuelEntry, 'categoryId' | 'ashContent'> | null {
  if (newCategory.id === oldCategory.id) {
    return null;
  }

  const oldStartValue = oldCategory.startValue ?? null;
  const untouched =
    row.ashContent == null || (oldStartValue !== null && isSameAshValue(row.ashContent, oldStartValue));

  const newStartValue = newCategory.startValue ?? null;
  const ashContent = untouched && newStartValue !== null ? newStartValue : (row.ashContent ?? null);

  return { categoryId: newCategory.id, ashContent };
}

/**
 * Gruppiert die flache Zeilenliste nach `categoryId` (D-02): jede uebergebene Kategorie erhaelt
 * genau eine Gruppe, in der uebergebenen Reihenfolge, auch wenn keine einzige Zeile dazu
 * existiert -- ungenutzte Kategorien bleiben sichtbar und leer statt hinter einem "Weitere
 * anzeigen"-Schalter zu verschwinden. Zeilen derselben Kategorie bleiben in Arrayreihenfolge.
 *
 * Eine Zeile mit einer `categoryId`, die in `categories` nicht (mehr) vorkommt (eine geloeschte
 * Kategorie, T-34-11), geht NICHT verloren: sie landet in einer synthetischen, LETZTEN Gruppe
 * ohne `category`. Kategorien lassen sich loeschen, der Verweis der laufenden Meldung bleibt
 * trotzdem gueltig -- verschwaende die Zeile aus der Anzeige, waere das stiller Datenverlust im
 * Formular, waehrend die Meldung sie weiterhin einreicht.
 */
export function groupRowsByCategory(
  rows: AlternativeFuelEntry[],
  categories: FuelAshCategory[],
): AlternativeFuelGroup[] {
  const groups: AlternativeFuelGroup[] = categories.map((category) => ({ category, rows: [] }));
  const byId = new Map<number, AlternativeFuelGroup>();
  groups.forEach((group) => byId.set(group.category!.id, group));

  let orphanGroup: AlternativeFuelGroup | undefined;

  rows.forEach((row, flatIndex) => {
    let target = byId.get(row.categoryId);
    if (!target) {
      if (!orphanGroup) {
        orphanGroup = { category: undefined, rows: [] };
      }
      target = orphanGroup;
    }
    target.rows.push({ row, flatIndex });
  });

  return orphanGroup ? [...groups, orphanGroup] : groups;
}

/** `1 Eintrag` bzw. `{n} Einträge` -- Beschriftung des Eintragszaehlers je Gruppenkopf. */
export function entryCountLabel(count: number): string {
  return count === 1 ? '1 Eintrag' : `${count} Einträge`;
}
